import { useTranslation } from 'react-i18next'

import { LottieSpinner } from '@/components/LottieSpinner/LottieSpinner'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import useMobileMediaQuery from '@/hooks/useMobileMediaQuery'
import { useListPaginatedOfTopicsQuery } from '@/store/api/RunReport/RunReport.query'
import type { listPaginatedOfTopicsList } from '@/store/api/RunReport/RunReport.types'

interface FocusedTopicsGuideSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export const FocusedTopicsGuideSheet = ({
  open,
  onOpenChange,
}: FocusedTopicsGuideSheetProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const isMobile = useMobileMediaQuery()
  const { data, isLoading } = useListPaginatedOfTopicsQuery(
    { page: 1, pageSize: 100 },
    { skip: !open },
  )

  const topics = data?.info.list ?? []

  const getTranslation = (
    topic: listPaginatedOfTopicsList,
    key: 'name' | 'description',
  ) => {
    const translation = topic.translations?.find(
      (tr) => tr.language === language,
    )
    return translation?.[key] ?? topic[key]
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="flex flex-col gap-4 max-md:h-[85vh] max-md:rounded-t-2xl sm:max-w-md"
      >
        <SheetHeader className="text-center sm:text-center">
          <SheetTitle>
            {t('dashboard.runReport.behaviorFlags.focusedTopicsGuide')}
          </SheetTitle>
          <SheetDescription>
            {t(
              'dashboard.runReport.behaviorFlags.focusedTopicsGuideSheetDescription',
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-1 min-h-0 flex-col gap-5 overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <LottieSpinner width={48} height={48} />
            </div>
          ) : (
            topics.map((topic) => (
              <div key={topic._id} className="flex flex-col gap-1">
                <p className="text-base font-medium text-neutralColor">
                  {getTranslation(topic, 'name')}
                </p>
                <p className="text-sm text-neutralColor80">
                  {getTranslation(topic, 'description')}
                </p>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
